package com.siriusxm.pia.views.unifiedaggregator

import androidx.compose.runtime.*
import com.siriusxm.cmc.success
import com.siriusxm.cmc.utils.*
import com.siriusxm.pia.Application
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.*
import com.siriusxm.pia.contentEditor
import com.siriusxm.pia.rest.unifiedaggregator.*
import com.siriusxm.pia.rest.unifiedaggregator.Entity
import com.siriusxm.pia.views.favorites.createEntityId
import com.siriusxm.pia.views.favorites.rememberFavorites
import com.siriusxm.pia.views.unifiedaggregator.entities.EntityEnhancerPlugin
import com.siriusxm.pia.views.unifiedaggregator.entities.EntityTranscriptView
import com.siriusxm.pia.views.unifiedaggregator.events.EventFilters
import com.siriusxm.pia.views.unifiedaggregator.events.entityEvents
import com.siriusxm.pia.views.unifiedaggregator.events.partialEventView
import com.siriusxm.pia.views.unifiedaggregator.relationships.entityRelationships
import com.siriusxm.pia.views.unifiedaggregator.services.EntityLinkFields
import com.siriusxm.pia.views.unifiedaggregator.services.EntityServicesView
import com.siriusxm.pia.views.unifiedaggregator.services.openInButton
import com.siriusxm.pia.views.accesscontrols.AccessControlsBadgeView
import com.siriusxm.pia.views.accesscontrols.toAccessControlVariants
import com.siriusxm.pia.views.unifiedaggregator.smithy.aggregatorOptions
import com.siriusxm.pia.views.unifiedaggregator.smithy.aggregatorSmithyEntityView
import com.siriusxm.smithy4kt.SmithyStructure
import com.siriusxm.unifiedcontent.console.aggregator.PartialRequest
import com.siriusxm.unifiedcontent.console.aggregator.rememberAllowedPartialRequests
import com.siriusxm.unifiedcontent.console.aggregator.rememberPublishablePartials
import unifiedcontent.aggregator.*
import contentingestion.unifiedmodel.*
import contentingestion.unifiedmodel.EntityType
import io.ktor.http.*
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.async
import kotlinx.coroutines.await
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.serialization.json.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.Color.red
import org.jetbrains.compose.web.dom.*
import smithy4kt.SmithyClientError
import kotlin.time.Clock
import kotlin.time.Duration.Companion.seconds
import kotlin.time.Instant

val accessControllable = listOf<String>() // listOf("league", "team", "show", "show-podcast")
val colorEditable = listOf("channel-linear", "channel-xtra", "team")

private const val ACCESS_CONTROLS_SHAPE_ID = "contentingestion.unifiedmodel#EntityAccessControls"

/**
 * Whether [entityType]'s Smithy schema declares an `accessControls` field of
 * shape [ACCESS_CONTROLS_SHAPE_ID]. Drives whether the "Access Controls"
 * detail is shown in the entity header - based on the entity type's actual
 * schema rather than a hardcoded list of type names, using the same
 * entityType -> shapeId -> entityModel.getShape(...) lookup chain
 * [aggregatorSmithyEntityView] uses to resolve a shape for rendering.
 */
fun AggregatorService.hasAccessControlsField(entityType: String): Boolean {
    val shapeId = this.entityType(entityType)?.shapeId ?: return false
    val shape = entityModel?.getShape(shapeId) as? SmithyStructure ?: return false
    return shape.allMembers["accessControls"]?.shape?.id == ACCESS_CONTROLS_SHAPE_ID
}

/**
 * Renders the detail view for a single aggregator entity.
 *
 * This component displays comprehensive information about an entity including:
 * - Entity header with image, type, version, and status
 * - Tabbed views for Atlas output, Catalog data, Incoming data, Events, etc.
 * - Action menu for republish, delete, restore, and favorites operations
 *
 * @param context The aggregator service context
 * @param destination the typed entity id and optional selected tab
 * @param filters the Events tab's filter selections, decoded from the URL query (UCA-1039). This
 *   page is the only place they are written: it navigates to itself with a new query, which is why
 *   [entityEvents] is fully controlled and holds no filter state of its own.
 */
@Composable
fun ServiceViewContext.aggregatorEntity(
    context: AggregatorService,
    destination: EntityDetailDestination,
    parameters: Map<String, String?> = emptyMap(),
    filters: EventFilters = EventFilters()
) {
    val id = destination.id
    val coroutineScope = rememberCoroutineScope()
    val favorites = rememberFavorites()
    var preparingPlayback by remember(id) { mutableStateOf(false) }

    var entities by remember { mutableStateOf<List<Entity>?>(null) }
    var incoming by remember { mutableStateOf<IncomingEntityResult?>(null) }
    var outgoing by remember { mutableStateOf<JsonObject?>(null) }
    var removed by remember { mutableStateOf<JsonObject?>(null) }

    var typedEntityId by remember { mutableStateOf<String?>(null) }
    var atlasId by remember { mutableStateOf<String?>(null) }

    /**
     * The enhancement plugins that are available for this entity.
     */
    var enhancers by remember { mutableStateOf<List<EntityEnhancerPlugin>>(emptyList()) }

    // The "Update …" menu: partials the console producer allows on this type that AVP lets this user
    // publish for this entity (UCA-1107/UCA-1152). The menu links to the partial editor with
    // `typedEntityId`, which becomes the modification id, so that is the id AVP is asked about.
    val availablePartials by context.rememberPublishablePartials(
        typedEntityId,
        entities?.maxByOrNull { it.version }
    )
    val userRoles by context.app.userRoles()
    var deleting by remember { mutableStateOf(false) }
    var restoring by remember { mutableStateOf(false) }
    // Derived reactively so it updates when either userRoles or incoming loads asynchronously
    val userAuthorizedForType = incoming?.entity?.data?.getString("type")?.let { entityType ->
        userRoles?.isAuthorizedToEdit(EntityType.fromName(entityType)) ?: false
    } ?: false

    suspend fun sendDelete(incoming: IncomingEntityResult?): ContentUpdateResponse? {
        val idToDelete = incoming?.entity?.data?.getString("id")
        val typeToDelete = incoming?.entity?.data?.getString("type")
        val versionToDelete = incoming?.entity?.data?.getString("version")
        if (idToDelete != null && typeToDelete != null && versionToDelete != null) {
            val producerForType = context.producers.find { it.allowedTypes?.contains(typeToDelete) == true }
            val contentUpdate = ContentUpdate(
                type = ContentUpdateType.REMOVE,
                timestamp = Clock.System.now(),
                producer = producerForType?.id,
                entity = buildJsonObject {
                    put("id", idToDelete)
                    put("type", typeToDelete)
                    put("version", versionToDelete)
                }
            )
            val responses = context.clientApi.postContentUpdate(listOf(contentUpdate))
            return responses.firstOrNull()
        }
        return null
    }

    fun loadEntity(id: String) {
        coroutineScope.launch {
            entities = null

            try {
                val mapping = context.clientApi.getMapping(id)
                atlasId = mapping.atlasId
                typedEntityId = mapping.sourceId
            } catch (_: Exception) {
                atlasId = null
                typedEntityId = null
            }

            val loadedEntities = context.clientApi.getEntityById(
                typedEntityId ?: id,
                true,
                includeMetadata = true
            ).asEntities()
            val default = loadedEntities.maxByOrNull { it.version }

            val incomingJob =
                if (default != null) {
                    async {
                        try {
                            context.clientApi.getIncomingEntityUi(
                                default.id, default.type, default.version
                            )
                        } catch (e: SmithyClientError) {
                            if (e.httpStatusCode == HttpStatusCode.NotFound.value) {
                                null
                            } else {
                                throw e
                            }
                        } catch (e: Exception) {
                            console.log("Unexpected exception (expecting SmithyClientError) on incoming", e)
                            null
                        }
                    }
                } else null

            val outgoingJob = if (default != null) {
                async {
                    try {
                        context.clientApi.getOutgoingEntityByVersion(
                            default.id, EntityType.fromName(default.type), default.version
                        ).jsonObject
                    } catch (e: SmithyClientError) {
                        if (e.httpStatusCode == HttpStatusCode.NotFound.value) {
                            null
                        } else {
                            throw e
                        }
                    } catch (e: Exception) {
                        console.log("Unexpected exception (expecting SmithyClientError) on outgoing", e)
                        null
                    }
                }
            } else {
                null
            }



            incoming = incomingJob?.await()
            outgoing = outgoingJob?.await()
            entities = loadedEntities

            val removedData = incomingJob?.await()?.entity?.data?.get("_removedData")
            removed = if (removedData == null || removedData == JsonNull) null else removedData.jsonObject

            // Same gate as the application's own tile (UCA-1196); hide-only, the application
            // enforces access itself.
            enhancers = context.enhancers.filter {
                Application.hasApplicationAccess(it.applicationDescriptor) &&
                    it.enabled(loadedEntities.firstOrNull()?.data, outgoing)
            }
        }

    }

    fun reloadEntity(id: String) {
        coroutineScope.launch {
            // reset the state and reload
            entities = null
            incoming = null
            outgoing = null
            atlasId = null
            typedEntityId = null

            try {
                loadEntity(id)
            } catch (e: Exception) {
                console.log(e)
            }
        }
    }

    LaunchedEffect(id) {
        loadEntity(id)
    }

    Style(JsonViewStyles)
    Style(SkeletonStyles)

    val entity = entities?.firstOrNull()
    val isRemoved = removed != null
    val removedTs = incoming?.entity?.data?.getString("_removed")?.let {
        Instant.parse(it)
    }

    serviceContentView({
        title = removed?.getString("name") ?: entity?.label
        subTitle = entity?.type?.let {
            context.entityType(it)?.name
        }
        // While the entity loads, show the heading as a skeleton so it occupies the
        // same position as the real title/subtitle and content doesn't shift on load.
        titleSkeleton = true
        subTitleSkeleton = true

        breadcrumbs {
            crumb("Aggregator", "aggregator")
            crumb("Entities", "aggregator/entities")
            val title = removed?.getString("name") ?: entities?.firstOrNull()?.label
            if (title != null) {
                crumb(title, null)
            }
        }

        // Custom action area with favorite star icon and actions menu (only shown when entity is loaded)
        actionArea {
            if (entity != null) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(8.px)
                    }
                }) {
                    // Favorite star icon
                    val entityId = createEntityId(entity.type, entity.id)
                    val isFavorite = favorites.isFavorite(entityId)
                    svgIcon(if (isFavorite) SVGIcons.starFilled else SVGIcons.star) {
                        size = 20.px
                        title = if (isFavorite) "Remove from Favorites" else "Add to Favorites"
                        style {
                            color(if (isFavorite) Color("#FFD700") else SXMUI.subtleTextColor.value())
                        }
                        action {
                            favorites.toggleFavorite(entityId)
                        }
                    }

                    // Playback clients submit only the opaque entity ID. The CMC server retrieves
                    // the authoritative entity and selects the type-specific preparation mechanism.
                    if (
                        entity.type == "track" ||
                        entity.type == "episode-audio" ||
                        entity.type == "episode-podcast" ||
                        (entity.type == "channel-linear" && Application.configuration.sxmPlaybackEnabled)
                    ) {
                        if (preparingPlayback) {
                            Div({
                                attr("role", "status")
                                attr("aria-label", "Preparing playback")
                                style {
                                    width(20.px)
                                    height(20.px)
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    justifyContent(JustifyContent.Center)
                                    color(SXMUI.subtleTextColor.value())
                                }
                            }) {
                                spinner(size = Size.SMALL)
                            }
                        } else {
                            svgIcon(SVGIcons.playerPlay) {
                                size = 20.px
                                title = "Play ${entity.label}"
                                style { color(SXMUI.subtleTextColor.value()) }
                                action {
                                    if (!preparingPlayback) {
                                        preparingPlayback = true
                                        // Use the view's scope: replacing the icon disposes its own scope.
                                        coroutineScope.launch {
                                            try {
                                                context.context.audioPlayer.playEntity(entity.id).await()
                                            } catch (cancellation: CancellationException) {
                                                throw cancellation
                                            } catch (error: Exception) {
                                                context.context.showError("Unable to play entity", error.message)
                                            } finally {
                                                preparingPlayback = false
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }

                    // Actions menu button
                    button {
                    title = "Actions"
                    popupAlign = PopupAlign.Right

                    if (!outgoing.isNullOrEmpty()) {
                subAction {
                    title = "Republish"
                    showProgressOnAction = true
                    action {
                        context.apiCall("Republish failed") {
                            outgoing?.let {
                                context.clientApi.republishEntity(
                                    entityId = it.entityId!!,
                                    entityType = EntityType.fromName(it.entityType!!),
                                    entityVersion = it.entityVersion!!,
                                    action = RepublishAction.republish
                                )
                                context.notifications.success("Entity republished successfully")
                            }
                        }
                    }
                }
            }

            // The Reprocess action is only for admins for now.
            if (context.isAdmin()) {
                subAction {
                    title = "Reprocess"
                    showProgressOnAction = true
                    action {
                        context.apiCall("Reprocess failed") {
                            if (typedEntityId != null && entity?.type != null) {
                                context.clientApi.republishEntity(
                                    entityId = typedEntityId!!,
                                    entityType = EntityType.fromName(entity.type),
                                    entityVersion = entity.version,
                                    action = RepublishAction.reprocess
                                )
                                context.notifications.success("Entity reprocessed successfully")
                            }
                        }
                    }
                }
            }

            // in the case that the entity is removed
            val removedData = incoming?.entity?.data?.get("_removedData")
            val removedObject = if (removedData == null || removedData == JsonNull) {
                null
            } else {
                removedData.jsonObject
            }

            if (removedObject != null) {
                // if the item has been removed, we should be able to restore it
                if (userAuthorizedForType) {
                    subAction {
                        title = "Restore Deleted Entity"
                        showProgressOnAction = true
                        action {
                            restoring = true
                        }
                    }
                }
            } else {
                if (userAuthorizedForType) {
                    subAction {
                        title = "Delete Entity"
                        showProgressOnAction = true
                        action {
                            deleting = true
                        }
                    }
                }
            }

            if (!availablePartials.isNullOrEmpty()) {
                subActionDivider()
                availablePartials?.forEach {
                    subAction {
                        title = "Update ${it.name}"
                        action {
                            context.navigate(
                                // `toString()` rather than a fallback: a null `typedEntityId` has always
                                // produced the literal segment "null" here. That is arguably wrong — line
                                // 138 uses `typedEntityId ?: id` for the same value — but this change is
                                // about encoding only.
                                "entity/${encodeURIComponent(typedEntityId.toString())}" +
                                    "/partials/${encodeURIComponent(it.id)}"
                            )
                        }
                    }
                }
            }
                }
                }
            }
        }
    }) {
        if (entities?.isEmpty() == true) {
            messageBox(
                "No entity with id $id was found. When an entity is removed, the reference is maintained for 60 days, after which is it completely removed from the system",
                MessageType.WARNING
            )
        } else if (entity == null) {
            entityLoadingSkeleton()
        } else {
            if (deleting) {
                confirmationDialog(
                    "Delete Entity",
                    "Are you sure you want to delete this entity?  This cannot easily be undone. Type `delete` in the field below to confirm.",
                    "Delete",
                    "delete"
                ) { deleted ->
                    if (deleted) {
                        context.apiCall("Delete entity failed") {
                            val idToDelete = incoming?.entity?.data?.getString("id")
                            val response = sendDelete(incoming)
                            if (response != null) {
                                if (response.code != 200) {
                                    context.context.showError("Unable to delete Id: ${response.entityId} - ${response.message}")
                                } else {
                                    reloadEntity(idToDelete!!)
                                }
                            }
                        }
                    }
                    deleting = false
                }
            }
            if (restoring) {
                confirmationDialog(
                    "Restore Entity",
                    "Are you sure you want to restore this entity?  Type `restore` in the field below to confirm.",
                    "Restore",
                    "restore"
                ) { acknowledged ->
                    if (acknowledged) {
                        context.apiCall("Restore deleted failed") {
                            incoming?.let {
                                val deletedId = incoming?.entity?.data?.getString("id")
                                val deletedType = incoming?.entity?.data?.getString("type")
                                val deletedVersion = incoming?.entity?.data?.getString("version")
                                if (deletedId != null && deletedType != null && deletedVersion != null) {
                                    context.clientApi.restoreDeletedIncomingEntity(
                                        deletedId,
                                        EntityType.fromName(deletedType),
                                        deletedVersion

                                    )
                                }
                            }
                        }
                        loadEntity(id)
                    }
                    restoring = false
                }
            }
            if (incoming != null) {
                val squareImage = getSquareImage(entity, outgoing)

                box {
                    Div({
                        classes(AggregatorStyles.entityHeader)
                    }) {
                        if (squareImage != null) {
                            Div {
                                val url = squareImage.resizedUrl(
                                    resize = ResizeParams.constrainToWidth(
                                        300,
                                        originalWidth = squareImage.width, originalHeight = squareImage.height
                                    )
                                )
                                Img(url, attrs = {
                                    classes(AggregatorStyles.entityHeaderImage)
                                })
                            }
                        }
                        Div({
                            style {
                                flex(1)
                            }
                        }) {

                            // provisional
                            val isProvisional =
                                (entity.type == "episode-audio" && incoming?.entity?.ts?.epochSeconds == 0L)
                            if (isProvisional) {
                                showProvisionalBadge()
                            }

                            detailGrid {
                                detail("ID") {
                                    Span({ classes(JsonViewStyles.copyableContentContainer) }) {
                                        Text(entity.id)
                                        copyContentIcon(entity.id)
                                    }
                                }
                                detail("Type") {
                                    A(href = "#aggregator/types/entities/${encodeURIComponent(entity.type)}") {
                                        Text(context.entityType(entity.type)?.name ?: entity.type)
                                    }
                                }

                                entity.lastModified?.let {
                                    detail("Updated") {
                                        // Render in the globally-selected timezone (header picker),
                                        // not the browser's system zone. Project the instant into
                                        // the chosen IANA zone first, then format the LocalDateTime.
                                        Text(it.toLocalDateTimeIn(currentTimeZoneId()).toLocalDateTimeString(true))
                                    }
                                }

                                detail("Data Sources") {
                                    val sources =
                                        listOfNotNull(incoming?.entity?.source) + (incoming?.partials?.mapNotNull { it.source }).orEmpty()
                                    val sourceProducers = sources.map { source ->
                                        context.producers.find { it.id == source } ?: ProducerDetails(source)
                                    }.distinctBy {
                                        it.id
                                    }.filter {
                                        it.id != "sxm:unified-aggregator:content-services-connector"
                                    }
                                    sourceProducers.forEach { producer ->
                                        Div {
                                            if (producer.name != null) {
                                                NavLink(ProducerDetail(producer.id)) {
                                                    Text(producer.name!!)
                                                }
                                            } else {
                                                Text(producer.name ?: producer.id)
                                            }
                                        }
                                    }
                                }

                                if (removedTs != null) {
                                    detail("Status") {
                                        Span({
                                            style {
                                                color(SXMUI.stopColor.value())
                                                fontWeight(700)
                                            }
                                        }) {
                                            Text("Removed")
                                        }
                                    }
                                }

                                if (context.hasAccessControlsField(entity.type)) {
                                    // Access Controls occupy their own full-width row so the
                                    // badge group (and expandable variant rows) have room to
                                    // lay out without crowding the other 33%-width details.
                                    Div({
                                        classes(DetailBoxStyles.detailContent)
                                        style {
                                            property("flex", "0 0 100%")
                                            width(100.percent)
                                        }
                                    }) {
                                        Div({ classes(DetailBoxStyles.detailLabel) }) {
                                            Text("Access Controls")
                                        }
                                        Div {
                                            AccessControlsBadgeView(
                                                outgoing?.get("accessControls")?.jsonObject
                                                    .toAccessControlVariants(includeImpliedDefault = true),
                                                key = id,
                                            )
                                        }
                                    }
                                }

                                detail("Links") {
                                    Div {
                                        // UCA-1277: configured links read entity fields by path,
                                        // e.g. {{atlas.cutId}}; one needing the published entity
                                        // stays hidden until it loads. data is the latest version's,
                                        // where reportingId has always been read from.
                                        val linkFields = EntityLinkFields(
                                            data = entities?.maxByOrNull { it.version }?.data,
                                            atlas = outgoing,
                                        )
                                        openInButton(
                                            context,
                                            atlasId,
                                            EntityType.fromName(entity.type),
                                            entity.name,
                                            typedEntityId,
                                            linkFields,
                                            parentShowId = entity.data.getString("show"),
                                        )
                                    }
                                    }
                            }
                        }
                    }
                }
            }
            tabView {
                selection = destination.tab?.id
                onSelect { selectedId ->
                    EntityTab.fromId(selectedId)?.let { selected ->
                        context.navigate(EntityDetailDestination(id, selected))
                    }
                    false
                }

                if (isRemoved) {
                    tab("Removed") {
                        Div {
                            aggregatorSmithyEntityView(context, removed!!, betweenToggleAndContent = {
                                removedTs?.let { ts ->
                                    Div({
                                        style {
                                            fontStyle("italic")
                                            fontSize(0.9.em)
                                            margin(0.px, 0.px, 0.5.em, 0.px)
                                        }
                                    }) {
                                        // Precise (millisecond) timestamp, for the same reason it's
                                        // shown on Incoming rows: diagnosing producer/region timing.
                                        Text(
                                            "${
                                                ts.toLocalDateTimeIn(currentTimeZoneId())
                                                    .toLocalDateTimeString(includeMilliseconds = true)
                                            }"
                                        )
                                    }
                                }
                            })
                        }
                    }
                }

                if (!isRemoved && outgoing != null) {
                    tab("Atlas", "outgoing") {
                        val partialOverrides = remember(incoming?.partials) {
                            incoming?.partials?.let { partials ->
                                buildPartialOverrideMap(partials) { partialId ->
                                    context.partial(partialId)
                                }
                            } ?: emptyMap()
                        }

                        outgoingContent(context, outgoing!!, partialOverrides) { partialId ->
                            context.navigate(
                                "entity/${encodeURIComponent(id)}/incoming?expand=${encodeURIComponent(partialId)}"
                            )
                        }
                    }
                }

                if (!isRemoved) {
                    tab("Catalog (Internal)", "catalog") {
                        aggregatorSmithyEntityView(context, entity.data)
                    }
                }

                if (incoming != null && !isRemoved) {
                    tab("Incoming", "incoming") {
                        entityIncomingView(context, incoming!!, parameters["expand"]) {
                            reloadEntity(id)
                        }
                    }
                }
                tab("Events", "events") {
                    entityEvents(
                        context,
                        entity.id,
                        filters = filters,
                        // Writing a filter is a navigation: same destination, new query. Push
                        // rather than replace, matching Producer's Events tab (UCA-1028), so
                        // browser Back steps back through filter states. Rebuilds the full
                        // entity/{id}/events path (not the tabNavigation base path above) because
                        // a filter change, unlike a tab switch, must keep the query.
                        onFiltersChange = { updated ->
                            context.navigate(
                                EntityDetailDestination(id, EntityTab.BuiltIn.Events),
                                updated.toQuery()
                            )
                        }
                    )
                }

                if (accessControllable.contains(entity.type)) {
                    tab("Access Control", "access") {
                        entityAccessControls(context, entity)
                    }
                }

                if (context.relationships.any {
                        (it.objectType.name == entity.type || it.subjectType.name == entity.type)
                                && it.subjectType.name != it.objectType.name
                    }) {
                    tab("Relationships", "relationships") {
                        entityRelationships(context, entity)
                    }
                }
                if (colorEditable.contains(entity.type)) {
                    if (context.viewer.contentEditor) {
                        tab("Color", "color") {
                            entityColors(context, entity) {
                                reloadEntity(id)
                            }
                        }
                    }
                }

                if (!isRemoved && outgoing != null) {
                    tab("Services") {
                        EntityServicesView(context.app, outgoing!!)
                    }
                }

                // Transcript tab - check for transcription.url in outgoing data
                if (!isRemoved && outgoing != null) {
                    val transcriptionUrl = try {
                        outgoing?.get("transcription")?.jsonObject?.get("url")?.jsonPrimitive?.contentOrNull
                    } catch (_: Exception) {
                        null
                    }

                    if (transcriptionUrl != null) {
                        tab("Transcript", "transcript") {
                            EntityTranscriptView(transcriptionUrl)
                        }
                    }
                }

                enhancers.forEach {
                    tab(it.name) {
                        it.render(entity.data, outgoing)
                    }
                }
            }
        }
    }
}

fun String.timestamp(): Instant? {
    return try {
        Instant.parse(this)
    } catch (_: Throwable) {
        null
    }
}

/**
 * Render the outgoing data with optional partial override indicators.
 * When partial overrides are provided, fields that have been modified by partials
 * will show a "layers" icon that can be clicked to navigate to the partial.
 */
@Composable
fun outgoingContent(
    aggregator: AggregatorService,
    outgoing: JsonObject,
    partialOverrides: PartialOverrideMap = emptyMap(),
    onPartialOverrideClick: ((String) -> Unit)? = null
) {
    val options = aggregatorOptions(aggregator).apply {
        // Inject override indicators inline inside each field label span
        if (partialOverrides.isNotEmpty()) {
            trailingLabelContent = { member ->
                val overrides = partialOverrides[member.name]
                if (!overrides.isNullOrEmpty()) {
                    partialOverrideIndicator(overrides, onPartialOverrideClick)
                }
            }
        }
    }
    aggregatorSmithyEntityView(aggregator, outgoing, options)
}

@Composable
fun showProvisionalBadge() {
    Div({
        style {
            position(Position.Fixed)
            top(150.px)
            left(0.px)
            width(100.percent)
            height(300.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("pointer-events", "none") // let clicks pass through
            property("z-index", "999")
        }
    }) {
        Span({
            style {
                color(red)
                fontSize(72.px)
                fontWeight("bold")
                opacity(0.2)
                textAlign("center")
            }
        }) {
            Text("PROVISIONAL")
        }
    }

}

/**
 * Skeleton placeholder shown while an entity is loading.
 *
 * Instead of a spinner, this renders shimmering grey blocks arranged to match the
 * shape of the loaded entity page: a header box containing the square artwork image
 * and two rows of label/value detail grids, followed by a thin tab-bar stub. The
 * matching layout means content appears to "fill in" rather than pop in after a spin.
 *
 * A short delay before the shimmer becomes visible (via [delayedRender]) avoids a
 * flash of placeholders on fast loads, matching the anti-flicker behavior of [spinner].
 */
@Composable
private fun entityLoadingSkeleton() {
    delayedRender(delay = 200) { visible ->
        Div({
            style {
                if (visible) {
                    property("visibility", "visible")
                } else {
                    property("visibility", "hidden")
                }
            }
        }) {
            box {
                Div({ classes(AggregatorStyles.entityHeader) }) {
                    // Square artwork placeholder, sized like the real entity image.
                    Div {
                        SkeletonBlock(width = 150.px, height = 150.px, radius = SXMUI.boxRadius.value())
                    }
                    // Detail columns to the right of the image.
                    Div({ style { flex(1) } }) {
                        SkeletonDetailGrid(count = 3)
                        SkeletonDetailGrid(count = 3)
                    }
                }
            }

            // Thin tab-bar stub beneath the header.
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(24.px)
                    marginTop(20.px)
                    padding(8.px, 4.px)
                }
            }) {
                repeat(5) {
                    SkeletonBlock(width = 70.px, height = 14.px)
                }
            }
        }
    }
}

/**
 * Displays the incoming entity data including base entity and partials.
 * @param expandedPartialId If provided, the partial with this ID will be initially expanded.
 */
@Composable
fun entityIncomingView(
    aggregatorContext: AggregatorService,
    incomingEntity: IncomingEntityResult,
    expandedPartialId: String? = null,
    reloadCb: suspend () -> Unit
) {
    var showDeleteSchedulePartialId by remember { mutableStateOf<String?>(null) }

    // Which existing partials this user may remove, asked of AVP in one batch with the same context the
    // aggregator builds from each removal (UCA-1107). Removing re-sends the stored modification, and
    // cancelling a scheduled partial uses the same partial's permission. Hide-only.
    val storedEntity = incomingEntity.entity.data
    val removableRequests = remember(incomingEntity) {
        (incomingEntity.partials.orEmpty().mapNotNull { event ->
            event.partialId?.let { PartialRequest(it, event.data) }
        } + incomingEntity.scheduled.orEmpty().mapNotNull { scheduled ->
            scheduled.partial?.let { PartialRequest(it.partialId, it.modification.jsonObject) }
        }).distinct()
    }
    val removable by aggregatorContext.rememberAllowedPartialRequests(removableRequests, storedEntity)

    @Composable
    fun incomingItemSummary(label: String, source: String?) {
        // Bottom-only margin: a top margin would push this first line down relative to
        // the row's expand chevron (which aligns to the text line box), making the
        // chevron appear out of line. Bottom spacing still separates stacked summary lines.
        Div({ style { margin(0.px, 0.px, 3.px, 0.px) } }) {
            Span({ style { fontWeight(700) } }) {
                Text(label)
            }

            Span {
                val producer = source?.let { aggregatorContext.producerById(it) }
                if (producer != null) {
                    Text(": ")
                    NavLink(ProducerDetail(producer.id)) {
                        Text(producer.name ?: producer.id)
                    }
                } else if (source != null) {
                    Text(": ")
                    Text(source)
                }
            }
        }
    }

    box({
        paddedContent = false
    }) {
        val allItems = listOfNotNull(incomingEntity.entity) + incomingEntity.partials.orEmpty()

        expandingRowTable<IncomingEvent> {
            items = allItems
            alwaysShowCollapsed = true

            if (expandedPartialId != null) {
                val itemToExpand = allItems.find { it.partialId == expandedPartialId }
                if (itemToExpand != null) {
                    initialExpanded = setOf(itemToExpand)
                }
            }

            collapsedContent { event ->
                if (event.partialId != null) {
                    val expiration = event.partialId.let { partialId ->
                        incomingEntity.scheduled?.filter { it.partial?.partialId == partialId }?.firstNotNullOfOrNull {
                            it.partial?.expirationTs
                        }
                    }
                    if (expiration != null) {
                        Div {
                            // Render in the globally-selected timezone (header picker).
                            Text("Expires: ${expiration.toLocalDateTimeIn(currentTimeZoneId()).toLocalDateTimeString()}")
                        }
                    }
                } else {
                    val expires = incomingEntity.scheduled?.firstNotNullOfOrNull {
                        it.update?.expirationTs
                    }

                    if (expires != null) {
                        Div {
                            // Render in the globally-selected timezone (header picker).
                            Text("To be removed: ${expires.toLocalDateTimeIn(currentTimeZoneId()).toLocalDateTimeString()}")
                        }
                    }
                }
                event.ts?.let { ts ->
                    Div({
                        style {
                            fontStyle("italic")
                            fontSize(0.9.em)
                        }
                    }) {
                        // Precise (millisecond) timestamp, since this tab is where producer/region
                        // timestamp discrepancies get diagnosed row-by-row.
                        Text(
                            "${
                                ts.toLocalDateTimeIn(currentTimeZoneId()).toLocalDateTimeString(includeMilliseconds = true)
                            }"
                        )
                    }
                }
                incomingItemSummary(event.partialId?.let {
                    aggregatorContext.partial(it)?.name
                } ?: "Base", event.source)
            }
            expandedContent { event ->
                Div {
                    if (event.partialId != null) {
                        val partial = PartialUpdate(
                            ContentUpdateType.UPDATE,
                            event.ts ?: Instant.DISTANT_PAST,
                            modification = event.data,
                            partialId = event.partialId ?: "Unknown",
                            operations = event.operations,
                            producer = event.source
                        )
                        val canRemovePartial = event.partialId?.let {
                            PartialRequest(it, event.data) in removable
                        } ?: false
                        partialEventView(
                            aggregatorContext,
                            partial,
                            if (canRemovePartial) {
                                {
                                    delay(3.seconds)
                                    reloadCb()
                                }
                            } else null
                        )
                    } else {
                        aggregatorSmithyEntityView(aggregatorContext, event.data)
                    }
                }
            }
        }
    }

    val scheduled = incomingEntity.scheduled?.filter {
        val publishTs = it.update?.publishTs ?: it.partial?.publishTs
        publishTs != null && publishTs > Clock.System.now()
    }.orEmpty()
    if (scheduled.isNotEmpty()) {
        box({
            title = "Scheduled"
            paddedContent = false
        }) {
            expandingRowTable<ScheduledContentUpdate> {
                items = scheduled
                alwaysShowCollapsed = true
                collapsedContent {
                    (it.partial?.publishTs ?: it.update?.publishTs)?.let {
                        Div {
                            // Render in the globally-selected timezone (header picker).
                            Text(it.toLocalDateTimeIn(currentTimeZoneId()).toLocalDateTimeString())
                        }
                    }
                    incomingItemSummary(it.partial?.partialId ?: "Base", it.source)
                }

                expandedContent {
                    Div {
                        val data = it.partial?.modification?.jsonObject
                            ?: it.update?.entity?.jsonObject
                            ?: buildJsonObject { }

                        val filtered = data.filterKeys {
                            it !in listOf("id", "type", "version")
                        }.let {
                            JsonObject(it)
                        }
                        jsonView(filtered)

                        val partialId = it.partial?.partialId
                        val timestamp = (it.partial?.publishTs ?: it.partial?.expirationTs)?.toString()
                        val canCancel = it.partial?.let { scheduledPartial ->
                            PartialRequest(scheduledPartial.partialId, scheduledPartial.modification.jsonObject) in removable
                        } ?: false
                        if (partialId != null && timestamp != null && canCancel) {

                            Text("Remove this scheduled partial: ")
                            iconAction("delete") {
                                showDeleteSchedulePartialId = partialId + "_" + timestamp
                            }

                            if (showDeleteSchedulePartialId == (partialId + "_" + timestamp)) {
                                val entityId = data.getString("id")
                                deleteConfirmationDialog(
                                    "Scheduled Partial \"$partialId\" for $entityId",
                                    onCancelCb = {
                                        showDeleteSchedulePartialId = null
                                    },
                                    onDeleteCb = {
                                        removeScheduledPartial(
                                            aggregatorContext,
                                            partialId,
                                            incomingEntity,
                                            timestamp
                                        )
                                        // we need to give time for the partial remove to take effect
                                        delay(3.seconds)
                                        reloadCb()
                                    }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

private suspend fun removeScheduledPartial(
    aggregatorContext: AggregatorService,
    partialId: String,
    incomingEntity: IncomingEntityResult,
    timestamp: String
) {
    val entity = incomingEntity.entity

    val entityId = entity.data.getString("id")
    val entityType = entity.data.getString("type")
    val entityVersion = entity.data.getString("version")


    if (entityId != null && entityType != null && entityVersion != null) {
        // We may need to escape
        aggregatorContext.clientApi.deleteScheduledPartial(
            entityId = entityId,
            entityType = EntityType.fromName(entityType),
            entityVersion = entityVersion,
            partialId = partialId,
            ts = Instant.parse(timestamp)
        )

        aggregatorContext.context.info(
            "Deleted scheduled partialId: $partialId " +
                    "for entity: $entityId with timestamp $timestamp.  " +
                    "If the app doesn't reload, you may need to refresh the page."
        )
    } else {
        aggregatorContext.context.showError("Unable to delete Schedule partial.")
    }
}


/**
 * In order to render in a more optimized fashion, we want to scale
 * them to a more appropriate size
 */
fun Image.resizedUrl(resize: ResizeParams? = null): String {
    return ImageRenderingHelper.getImageUrl(
        Application.configuration.atlasImageServerBaseUrl,
        url,
        resize ?: ResizeParams(this.width ?: 300, this.height ?: 300),
        ImageFormat.JPEG
    )
}

/**
 * Get the image override if available
 */
private fun getSquareImage(entity: Entity, outgoing: JsonObject?): Image? {
    val defaultImage = entity.images[ImagePurpose.TILE]?.get(ImageAspectRatio.ASPECT_1X1)?.default

    val outgoingImage = try {
        outgoing
            ?.get("images")?.jsonObject
            ?.get("tile")?.jsonObject
            ?.get("aspect_1x1")?.jsonObject
            ?.get("default")?.jsonObject
    } catch (_: Exception) {
        null
    }
    val outGoingImageUrl = outgoingImage?.get("url")?.jsonPrimitive?.contentOrNull

    return if (outGoingImageUrl != null && defaultImage?.url != outGoingImageUrl) {
        Image(
            url = outGoingImageUrl,
            width = outgoingImage["width"]?.jsonPrimitive?.intOrNull,
            height = outgoingImage["height"]?.jsonPrimitive?.intOrNull,
            dominantColors = null
        )
    } else {
        defaultImage
    }
}


/**
 * Convert a string to a slug.
 */
fun String?.slugify(): String? {
    return (this ?: return null)
        .lowercase() // Convert to lowercase
        .replace(Regex("[^a-z0-9\\s-]"), "") // Remove non-alphanumeric characters (except spaces and hyphens)
        .replace(Regex("\\s+"), "-") // Replace spaces with hyphens
        .replace(Regex("-+"), "-") // Replace multiple hyphens with a single hyphen
        .trim('-') // Trim leading and trailing hyphens
}
